import { useSectionAccess } from "../auth/useSectionAccess";
import { useEffect, useMemo, useState } from "react";
import { TableSkeleton, Spinner } from "../components/Loading";
import { Pager } from "../components/Pager";
import { DateRangeFilter } from "../components/DateRangeFilter";
import type { FilterOption } from "../api/admin";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { ordersApi, statusBadgePalette, type Order, type OrderStatus } from "../api/orders";
import { BOARD_STATUS_ORDER, boardColumns, boardTargets, canDropTo } from "./ordersBoard";
import type { ApiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../components/Toast";
import { useConfirm, usePrompt } from "../components/ConfirmDialog";
import { OrderStatusBadge } from "../components/OrderStatusBadge";
import { ExportButton } from "../components/ExportButton";
import { Select } from "../components/Select";
import { flagEmoji, countryName } from "../i18n/countries";
import Ic from "../components/icons.jsx";
import { ImportOrderModal } from "./orders/ImportOrderModal";
import { formatMoney, intlLocale } from "../lib/format";
import { usePage } from "../lib/usePage";
import { KanbanColumn, boardScroll } from "../components/KanbanColumn";

type SortKey = "submitted_desc" | "submitted_asc" | "customer" | "delivery";

/** Koliko redova ide u jednu stranu. Spisak nosi celu istoriju, pa nikad nije ceo. */
/** Koliko kartica kolona pokaže odjednom; ostatak na dugme. */
const COLUMN_SIZE = 12;
const PAGE_SIZE = 50;

const PIPELINE: OrderStatus[] = [
  "DRAFT",
  "SUBMITTED",
  "IN_REVIEW",
  "APPROVED",
  "SHIPPED",
  "INVOICED",
];

/**
 * Orders list — sales-focused dashboard. For SALES this is the single most
 * important screen, so it leads with action: a KPI strip (Needs review /
 * Approved / Shipped 30d / Late), status chips with counts, and a table
 * that shows the *customer* prominently (with country flag) plus an overdue
 * indicator on the requested-delivery column.
 */
export default function OrdersPage() {
  const { canWrite, canFull } = useSectionAccess("orders");
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { state: authState, hasPermission } = useAuth();
  // Suspended company: the server rejects order creation, so
  // don't offer the entry points at all.
  const suspended = authState.status === "authenticated" && authState.user.companySuspended;
  // Bankom's people review what waits here; a customer's own orders wait on Bankom.
  const internal = authState.status === "authenticated" && authState.user.internal;
  const locale = i18n.resolvedLanguage ?? "en";

  const [searchParams, setSearchParams] = useSearchParams();
  // List is the default view; the kanban board is opt-in via ?view=board so a
  // shared/bookmarked link keeps whichever view was chosen.
  const view: "list" | "board" = searchParams.get("view") === "board" ? "board" : "list";
  const setView = (v: "list" | "board") => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (v === "board") next.set("view", "board"); else next.delete("view");
      return next;
    }, { replace: true });
  };

  const [items, setItems] = useState<Order[] | null>(null);
  const [statusFilter, setStatusFilter] = useState<OrderStatus | "">("");
  const [search, setSearch] = useState("");
  const [countryFilter, setCountryFilter] = useState<string>("");
  const [sort, setSort] = useState<SortKey>("submitted_desc");
  const [importOpen, setImportOpen] = useState(false);
  const [total, setTotal] = useState(0);
  const [iznosPoStatusu, setIznosPoStatusu] = useState<Record<string, Record<string, number>>>({});
  const [evriPoStatusu, setEvriPoStatusu] = useState<Record<string, number>>({});
  const [bezKursaPoStatusu, setBezKursaPoStatusu] = useState<Record<string, number>>({});
  const [poStatusu, setPoStatusu] = useState<Record<string, Order[]>>({});
  const [trazenoPoStatusu, setTrazenoPoStatusu] = useState<Record<string, number>>({});
  const [page, setPage] = usePage();
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [serverCounts, setServerCounts] = useState<Record<string, number> | null>(null);
  const [serverPokazatelji, setServerPokazatelji] = useState<{ shipped30: number; late: number } | null>(null);
  const [countryOptions, setCountryOptions] = useState<FilterOption[]>([]);
  const [busy, setBusy] = useState(false);

  /**
   * Filter, redosled i brojanje rade u bazi. Spisak nosi istoriju iz starog sistema —
   * četrdeset hiljada porudžbina, svaka sa svojim stavkama — pa jedna strana nikad ne
   * sme da bude ceo spisak.
   */
  async function load(strana = page) {
    setBusy(true);
    try {
      const r = await ordersApi.list({
        status: statusFilter || undefined,
        country: countryFilter || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        q: search.trim() || undefined,
        sort,
        page: strana,
        size: PAGE_SIZE,
      });
      setItems(r.content);
      setTotal(r.totalElements);
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  const confirm = useConfirm();
  const prompt = usePrompt();
  const canTransition = canWrite && hasPermission("orders:transition");
  const canCreate = canWrite && hasPermission("orders:create");
  const canApprove = canFull && authState.status === "authenticated" && authState.user.roles.some((r) => ["ADMIN", "SUPERADMIN", "SALES_DIRECTOR"].includes(r));

  /**
   * Board drag&drop move — the exact same guarded transitions as the detail
   * page's buttons (FSM-legal targets only, mandatory reject reason, cancel
   * confirmation), applied optimistically with rollback on API refusal.
   */
  async function moveOrderTo(order: Order, to: OrderStatus) {
    if (!canTransition || order.fromErp || (to === "APPROVED" &&(!canApprove || order.createdBy === (authState.status === "authenticated" ? authState.user.id : null)))) return;
    let notes: string | undefined;
    if (to === "REJECTED") {
      const reason = await prompt({
        title: t("orderEditor.reject.title", "Reject order"),
        message: t("orderEditor.reject.message", "Explain why {{orderNumber}} is being rejected. The customer sees this reason and can clone the order to a new draft.", { orderNumber: order.orderNumber }),
        placeholder: t("orderEditor.reject.placeholder", "Reason for rejection…"),
        multiline: true,
        required: true,
        danger: true,
        confirmLabel: t("orderEditor.transition.reject", "Reject"),
      });
      if (reason == null || !reason.trim()) return;
      notes = reason.trim();
    } else if (to === "CANCELLED") {
      if (!(await confirm({ message: t("orderEditor.confirm.cancel", "Cancel {{orderNumber}}? This cannot be undone.", { orderNumber: order.orderNumber }), danger: true }))) return;
    }
    const prev = order.status;
    setItems((p) => p?.map((o) => (o.id === order.id ? { ...o, status: to } : o)) ?? null);
    try {
      const updated = await ordersApi.transition(order.id, to, notes);
      setItems((p) => p?.map((o) => (o.id === order.id ? updated : o)) ?? null);
      toast.success(t("orderEditor.toast.orderMovedTo", "Order moved to {{status}}.", { status: t(`orders.status.${statusKey(to)}`, { defaultValue: to }) }));
    } catch (err) {
      toast.error((err as ApiError).message);
      setItems((p) => p?.map((o) => (o.id === order.id ? { ...o, status: prev } : o)) ?? null);
    }
  }

  useEffect(() => {
    ordersApi.countries().then(setCountryOptions).catch(() => setCountryOptions([]));
  }, []);

  // Brojači i zbirovi idu sa istim filterima kao kartice, inače zaglavlje i spisak govore o
  // različitim skupovima.
  useEffect(() => {
    ordersApi
      .counts({
        country: countryFilter || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        q: search.trim() || undefined,
      })
      .then((r) => {
        setServerCounts(r.counts ?? null);
        setServerPokazatelji(r.indicators ?? null);
        setIznosPoStatusu(r.amounts ?? {});
        setEvriPoStatusu(r.eur ?? {});
        setBezKursaPoStatusu(r.noRate ?? {});
      })
      .catch(() => {
        setServerCounts(null); setServerPokazatelji(null);
        setIznosPoStatusu({}); setEvriPoStatusu({}); setBezKursaPoStatusu({});
      });
  }, [countryFilter, dateFrom, dateTo, search]);

  // Tabla ne crta učitanu stranicu nego traži vrh svake kolone posebno. Sa 44.307 porudžbina
  // stranica od pedeset ne bi napunila ni jednu kolonu, a brojevi u zaglavlju bi govorili o tih
  // pedeset umesto o svima.
  useEffect(() => {
    if (view !== "board") return;
    let otkazano = false;
    const zajednicko = {
      country: countryFilter || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      q: search.trim() || undefined,
      sort,
    };
    void Promise.all(
      BOARD_STATUS_ORDER.map((st) =>
        ordersApi
          .list({ ...zajednicko, status: st, size: trazenoPoStatusu[st] ?? COLUMN_SIZE })
          .then((r) => [st, r.content] as const)
          .catch(() => [st, [] as Order[]] as const),
      ),
    ).then((parovi) => {
      if (otkazano) return;
      setPoStatusu(Object.fromEntries(parovi) as Record<OrderStatus, Order[]>);
    });
    return () => { otkazano = true; };
  }, [view, countryFilter, dateFrom, dateTo, search, sort, trazenoPoStatusu]);

  // Povratak na prvu stranu je promena strane kao i svaka druga. Dok su ovo bila dva efekta, onaj za
  // stranu je preskakao nulu da ne bi dvaput ucitao pri promeni filtera — pa se sa druge strane na
  // prvu nije ucitavalo nista, a tabela je ostajala na staroj strani dok je stranicar pokazivao prvu.
  useEffect(() => {
    setPage(0);
  }, [statusFilter, countryFilter, search, sort, dateFrom, dateTo]);

  // Odlaganje stoji samo zbog otkucanog slova; promena strane ide odmah.
  useEffect(() => {
    const id = setTimeout(() => void load(page), search ? 450 : 0);
    return () => clearTimeout(id);
  }, [statusFilter, countryFilter, search, sort, dateFrom, dateTo, page]);

  const today = useMemo(() => new Date(new Date().toDateString()), []);

  // Brojači dolaze sa servera, nad celim vidljivim skupom. Računati ih iz učitane strane
  // znači brojati pedeset redova koje je strana slučajno donela.
  const counts = useMemo(() => {
    if (serverCounts) return serverCounts;
    const c: Record<string, number> = { ALL: 0 };
    if (!items) return c;
    c.ALL = items.length;
    for (const o of items) c[o.status] = (c[o.status] ?? 0) + 1;
    return c;
  }, [items, serverCounts]);

  // All four cards come from the server, over the same visible set the list draws from. A figure a
  // reader takes for fact must not depend on which page happens to be open: computed from the loaded
  // page, "approved" could never exceed the fifty rows that page holds, while the true count was ten
  // thousand.
  const kpis = useMemo(() => {
    const c = serverCounts;
    return {
      needsReview: (c?.SUBMITTED ?? 0) + (c?.IN_REVIEW ?? 0),
      approved: c?.APPROVED ?? 0,
      shipped30: serverPokazatelji?.shipped30 ?? 0,
      late: serverPokazatelji?.late ?? 0,
    };
  }, [serverCounts, serverPokazatelji]);

  // Države dolaze sa servera, nad celim vidljivim skupom: iz učitane strane bi se videle
  // samo one koje je ta strana slučajno donela.
  const countriesPresent = countryOptions.map((o) => o.value);

  const customersCount = useMemo(() => {
    const s = new Set<string>();
    items?.forEach((o) => s.add(o.companyId));
    return s.size;
  }, [items]);

  // Baza je već filtrirala i poređala; ono što je stiglo jeste odgovor.
  const filtered = items ?? [];

  const clearAll = () => {
    setStatusFilter("");
    setCountryFilter("");
    setSearch("");
    setDateFrom("");
    setDateTo("");
  };

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <header style={pageHead}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, color: "var(--ink)" }}>{t("orders.list.title")}</h1>
          <div style={subText}>
            {items === null
              ? t("common.loading")
              : t("orders.list.summary", "{{orders}} orders · {{customers}} customers · {{countries}} countries", {
                  orders: items.length.toLocaleString(locale),
                  customers: customersCount,
                  countries: countriesPresent.length,
                })}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <ExportButton
            path={`/exports/orders.csv${statusFilter ? `?status=${statusFilter}` : ""}`}
            label={t("orders.list.exportCsv")}
            filename="orders.csv"
          />
          {hasPermission("section:order_imports:write") && hasPermission("orders:import") && (
            <button type="button" className="btn" onClick={() => setImportOpen(true)}>
              <Ic.upload /> {t("orders.import.button")}
            </button>
          )}
          {canCreate && !suspended && (
            <Link to="/orders/new" className="btn primary" style={{ textDecoration: "none" }}>
              <Ic.plus /> {t("orders.list.newOrder")}
            </Link>
          )}
        </div>
      </header>

      {importOpen && <ImportOrderModal onClose={() => setImportOpen(false)} />}

      <div style={{ display: "flex", gap: 6 }}>
        <button type="button" className={view === "board" ? "btn primary sm" : "btn sm"} onClick={() => setView("board")}>
          <Ic.grid /> {t("orders.view.board")}
        </button>
        <button type="button" className={view === "list" ? "btn primary sm" : "btn sm"} onClick={() => setView("list")}>
          <Ic.list /> {t("orders.view.list")}
        </button>
      </div>

      <div style={kpiGrid}>
        <KpiCard
          label={t("orders.kpi.needsReview.label", "Needs review")}
          value={kpis.needsReview}
          accent="var(--warn)"
          bg="var(--warn-bg)"
          icon={<Ic.alert />}
          onClick={() => setStatusFilter("SUBMITTED")}
          active={statusFilter === "SUBMITTED"}
          hint={internal
            ? t("orders.kpi.needsReview.hint", "Submitted by customers, waiting on you")
            : t("orders.kpi.needsReview.hintCustomer", "Sent to Bankom, waiting for sales review")}
        />
        <KpiCard
          label={t("orders.kpi.approved.label", "Approved")}
          value={kpis.approved}
          accent="var(--tone-cyan)"
          bg="var(--tone-cyan-bg)"
          icon={<Ic.check />}
          onClick={() => setStatusFilter("APPROVED")}
          active={statusFilter === "APPROVED"}
          hint={t("orders.kpi.approved.hint", "Approved, awaiting shipment")}
        />
        <KpiCard
          label={t("orders.kpi.shipped30.label", "Shipped 30d")}
          value={kpis.shipped30}
          accent="var(--ok)"
          bg="var(--ok-bg)"
          icon={<Ic.truck />}
          hint={t("orders.kpi.shipped30.hint", "Closed in the last 30 days")}
        />
        <KpiCard
          label={t("orders.kpi.late.label", "Late delivery")}
          value={kpis.late}
          accent="var(--err)"
          bg="var(--err-bg)"
          icon={<Ic.calendar />}
          hint={t("orders.kpi.late.hint", "Requested-delivery date already passed")}
          danger={kpis.late > 0}
        />
      </div>

      <div style={chipBar}>
        <Chip
          label={`${t("orders.filter.all", "All")} ${counts.ALL ?? 0}`}
          active={!statusFilter}
          onClick={() => setStatusFilter("")}
        />
        {PIPELINE.map((s) => {
          const n = counts[s] ?? 0;
          if (n === 0 && statusFilter !== s) return null;
          return (
            <Chip
              key={s}
              label={`${t(`orders.status.${statusKey(s)}`, { defaultValue: s })} ${n}`}
              active={statusFilter === s}
              onClick={() => setStatusFilter(statusFilter === s ? "" : s)}
            />
          );
        })}
      </div>

      <div style={filterBar}>
        <div style={searchWrap}>
          <span className="ord-search-icon" style={searchIcon}><Ic.search /></span>
          <input
            className="input"
            placeholder={t("orders.filter.searchPlaceholder", "Search by customer or order number…")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ paddingLeft: 32, height: 36, width: "100%" }}
          />
        </div>
        <Select<string>
          value={countryFilter}
          onChange={setCountryFilter}
          options={[
            { value: "", label: t("orders.filter.allCountries", "All countries") },
            ...countriesPresent.map((c) => ({ value: c, label: `${flagEmoji(c)} ${countryName(c, locale)}` })),
          ]}
          style={{ minWidth: 140 }}
        />
        <Select<SortKey>
          value={sort}
          onChange={setSort}
          options={[
            { value: "submitted_desc", label: t("orders.sort.newest", "Sort: Newest first") },
            { value: "submitted_asc", label: t("orders.sort.oldest", "Sort: Oldest first") },
            { value: "customer", label: t("orders.sort.customer", "Sort: Customer A→Z") },
            { value: "delivery", label: t("orders.sort.delivery", "Sort: Delivery date") },
          ]}
          style={{ minWidth: 160 }}
        />
        {busy && <Spinner size={15} style={{ color: "var(--ink-3)" }} />}
      </div>

      <div style={filterBar}>
        <DateRangeFilter
          from={dateFrom}
          to={dateTo}
          onChange={(od, do_) => { setDateFrom(od); setDateTo(do_); }}
        />
        {(search || statusFilter || countryFilter || dateFrom || dateTo) && (
          <button type="button" className="btn sm" onClick={clearAll}>
            {t("orders.filter.clear", "Clear filters")}
          </button>
        )}
      </div>

      <Pager page={page} size={PAGE_SIZE} total={total} busy={busy} onPage={setPage} />

      {items === null ? (
        <TableSkeleton rows={8} cols={6} label={t("common.loading")} />
      ) : filtered.length === 0 ? (
        <div style={emptyCard}>
          <div style={{ fontSize: 28, marginBottom: 4 }}>📦</div>
          <b style={{ color: "var(--ink-2)" }}>{t("orders.empty.title", "No orders match.")}</b>
          <div style={{ color: "var(--ink-3)", fontSize: 13, marginTop: 4 }}>
            {items.length === 0
              ? t("orders.empty.none", "No orders have come in for your customers yet.")
              : t("orders.empty.filtered", "Try clearing the filters or searching for a different customer.")}
          </div>
          {items.length === 0 && canCreate && !suspended ? (
            <Link to="/orders/new" className="btn primary" style={{ textDecoration: "none", marginTop: 12 }}>
              <Ic.plus /> {t("orders.list.newOrder")}
            </Link>
          ) : (
            <button type="button" className="btn sm" onClick={clearAll} style={{ marginTop: 12 }}>
              {t("orders.filter.clear", "Clear filters")}
            </button>
          )}
        </div>
      ) : view === "board" ? (
        <OrdersBoard
          poStatusu={poStatusu}
          brojevi={serverCounts}
          iznosi={iznosPoStatusu}
          evri={evriPoStatusu}
          bezKursa={bezKursaPoStatusu}
          naJos={(st) => setTrazenoPoStatusu((p) => ({ ...p, [st]: (p[st] ?? COLUMN_SIZE) + COLUMN_SIZE }))}
          today={today}
          locale={locale}
          t={t}
          onOpen={(oid) => navigate(`/orders/${oid}`)}
          canTransition={canTransition}
          canApprove={canApprove}
          viewerId={authState.status === "authenticated" ? authState.user.id : null}
          onMove={(o, to) => void moveOrderTo(o, to)}
        />
      ) : (
        <div className="card" style={{ overflow: "hidden", padding: 0 }}>
          <table style={tbl} className="list-card">
            <thead>
              <tr>
                <th style={th}>{t("orders.table.orderNumber", "Order #")}</th>
                <th style={th}>{t("orders.table.customer", "Customer")}</th>
                <th style={{ ...th, textAlign: "right", paddingRight: 16 }}>{t("orders.table.lines", "Lines")}</th>
                <th style={th}>{t("orders.table.status", "Status")}</th>
                <th style={th}>{t("orders.table.requestedDelivery", "Requested delivery")}</th>
                <th style={th}>{t("orders.table.submitted", "Submitted")}</th>
                <th style={th} aria-label={t("orders.table.open", "Open")} />
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => {
                const late =
                  !!o.requestedDeliveryDate
                  && new Date(o.requestedDeliveryDate) < today
                  && o.status !== "INVOICED"
                  && o.status !== "CANCELLED"
                  && o.status !== "REJECTED";
                const needsAction = o.status === "SUBMITTED" || o.status === "IN_REVIEW";
                const fresh =
                  needsAction
                  && !!o.submittedAt
                  && Date.now() - new Date(o.submittedAt).getTime() < 24 * 3600 * 1000;
                return (
                  <tr
                    key={o.id}
                    className={needsAction ? "ord-row-action" : undefined}
                    onClick={() => navigate(`/orders/${o.id}`)}
                    style={{
                      ...rowStyle,
                      cursor: "pointer",
                      ...(needsAction
                        ? { background: "var(--warn-bg)", boxShadow: "inset 3px 0 0 var(--warn)" }
                        : {}),
                    }}
                  >
                    <td style={td} data-label={t("orders.table.orderNumber", "Order #")}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {fresh && (
                          <span
                            className="ord-fresh-dot"
                            title={t("orders.row.freshTitle", "New — submitted in the last 24h")}
                            aria-label={t("orders.row.freshLabel", "New")}
                          />
                        )}
                        <code style={mono}>{o.orderNumber}</code>
                      </div>
                      {o.portalNumber && (
                        <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 2 }}>
                          {t("orders.row.requestNumber", "request {{number}}", { number: o.portalNumber })}
                        </div>
                      )}
                    </td>
                    <td style={td} data-label={t("orders.table.customer", "Customer")}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 18 }}>{o.companyCountry ? flagEmoji(o.companyCountry) : "🏳"}</span>
                        <div>
                          <div style={{ fontWeight: 600, color: "var(--ink)" }}>{o.companyName}</div>
                          <div style={{ fontSize: 11, color: "var(--ink-4)" }}>
                            {o.companyCountry ?? "—"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ ...td, textAlign: "right", paddingRight: 16, color: "var(--ink-2)", fontVariantNumeric: "tabular-nums" }} data-label={t("orders.table.lines", "Lines")}>
                      {o.lines.length}
                    </td>
                    <td style={td} data-label={t("orders.table.status", "Status")}>
                      <OrderStatusBadge status={o.status} />
                    </td>
                    <td style={td} data-label={t("orders.table.requestedDelivery", "Requested delivery")}>
                      {o.requestedDeliveryDate ? (
                        <span style={{ color: late ? "var(--err)" : "var(--ink-2)", fontWeight: late ? 600 : 400 }}>
                          {fmtDate(o.requestedDeliveryDate, locale)}
                          {late && (
                            <span className="ord-late-icon" style={lateChip}>
                              <Ic.alert /> {t("orders.row.overdue", "overdue")}
                            </span>
                          )}
                        </span>
                      ) : (
                        <span style={{ color: "var(--ink-4)" }}>—</span>
                      )}
                    </td>
                    <td style={{ ...td, color: "var(--ink-3)", fontSize: 12.5 }} data-label={t("orders.table.submitted", "Submitted")}>
                      {o.submittedAt ? relTime(o.submittedAt, locale, t) : <span style={{ color: "var(--ink-4)" }}>{t("orders.row.draft", "draft")}</span>}
                    </td>
                    <td style={{ ...td, textAlign: "right" }} className="card-actions">
                      <Link to={`/orders/${o.id}`} className="btn sm" style={{ textDecoration: "none" }} onClick={(e) => e.stopPropagation()}>
                        {t("orders.row.open", "Open")} <Ic.arrowRight />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pager page={page} size={PAGE_SIZE} total={total} busy={busy} onPage={setPage} />
        </div>
      )}
    </div>
  );
}

const money = (n: number, ccy: string) =>
  formatMoney(n, ccy, undefined, { maximumFractionDigits: 0 });

/**
 * Kanban board over the same filtered orders the list shows. Columns are the
 * order lifecycle (grouped by {@link groupOrdersByStatus}). Cards drag only
 * along FSM-legal transitions (canDropTo) and only for callers holding
 * orders:transition — the identical moves the detail page's buttons offer, so
 * the permanent sales-approval gate (project rule) is fully preserved:
 * no gate-skipping drop even exists as a target. While dragging, the dragged
 * order's legal-but-empty side-exit columns (e.g. REJECTED) materialise so
 * the drop target is visible. Clicking a card still opens the order.
 */
function OrdersBoard({
  poStatusu, brojevi, iznosi, evri, bezKursa, naJos, today, locale, t, onOpen, canTransition, canApprove, viewerId, onMove,
}: {
  /** Vrh svake kolone, kako ga je vratio server. */
  poStatusu: Record<string, Order[]>;
  /** Broj po statusu nad celim vidljivim skupom, ne nad učitanim karticama. */
  brojevi: Record<string, number> | null;
  iznosi: Record<string, Record<string, number>>;
  evri: Record<string, number>;
  /** Per status, orders in a currency without any exchange rate, left out of the euro sum. */
  bezKursa: Record<string, number>;
  naJos: (status: OrderStatus) => void;
  today: Date;
  locale: string;
  t: TFunction;
  onOpen: (orderId: string) => void;
  canTransition: boolean;
  canApprove: boolean;
  viewerId: string | null;
  onMove: (order: Order, to: OrderStatus) => void;
}) {
  const [dragging, setDragging] = useState<Order | null>(null);
  // Kolona se prikazuje ako je deo redovnog toka, ako u njoj nečega ima, ili ako je dozvoljeno
  // odredište za ono što se upravo vuče (da prazan REJECTED osvane kao meta).
  const targets = (order: Order) => boardTargets(order, canApprove, viewerId);
  const meta = dragging && canTransition ? new Set<OrderStatus>(targets(dragging)) : undefined;
  const columns = boardColumns(poStatusu, brojevi, meta);
  return (
    <div style={boardScroll}>
      {columns.map((col) => {
        const pal = statusBadgePalette(col.status);
        const canDrop = !!dragging && canTransition && canDropTo(dragging.status, col.status) && targets(dragging).includes(col.status);
        return (
          <KanbanColumn
            key={col.status}
            naziv={t(`orders.status.${statusKey(col.status)}`, { defaultValue: col.status })}
            boja={pal.fg}
            broj={col.count}
            eur={evri[col.status]}
            bezKursa={bezKursa[col.status]}
            poValutama={iznosi[col.status]}
            jezik={locale}
            moze={canDrop}
            onDrop={() => { const d = dragging; setDragging(null); if (d) onMove(d, col.status); }}
          >
              {col.count === 0 ? (
                <div style={boardEmpty}>{t("orders.board.emptyColumn", "No orders")}</div>
              ) : (
                col.orders.map((o) => {
                  const late =
                    !!o.requestedDeliveryDate && new Date(o.requestedDeliveryDate) < today
                    && o.status !== "INVOICED" && o.status !== "CANCELLED" && o.status !== "REJECTED";
                  const needsAction = o.status === "SUBMITTED" || o.status === "IN_REVIEW";
                  const draggable = canTransition && targets(o).length > 0;
                  return (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => onOpen(o.id)}
                      draggable={draggable}
                      onDragStart={(e) => {
                        if (!draggable) return;
                        e.dataTransfer.setData("text/plain", o.id);
                        e.dataTransfer.effectAllowed = "move";
                        setDragging(o);
                      }}
                      onDragEnd={() => { setDragging(null); }}
                      style={{
                        ...boardCard,
                        ...(needsAction ? { boxShadow: "inset 3px 0 0 var(--warn)" } : {}),
                        ...(draggable ? { cursor: "grab" } : {}),
                        ...(dragging?.id === o.id ? { opacity: 0.5, transform: "scale(0.98)" } : {}),
                      }}
                      className="ord-board-card"
                    >
                      {/* Isti raspored kao na kartici ponude: ko, koliko, pa podnožje. */}
                      <div style={kupacKartice}>
                        <span style={{ fontSize: 15, flex: "none" }}>
                          {o.companyCountry ? flagEmoji(o.companyCountry) : "🏳"}
                        </span>
                        <span style={imeKupca} title={o.companyName}>{o.companyName}</span>
                      </div>

                      <div style={iznosKartice}>{money(o.orderTotal, o.currency)}</div>

                      <span style={{ fontSize: 11.5, color: "var(--ink-3)" }}>
                        {t("orders.board.lines", { count: o.lines.length, defaultValue: "{{count}} stavki" })}
                      </span>

                      <div style={podnozjeKartice}>
                        <code style={brojKartice} title={o.orderNumber}>{o.orderNumber}</code>
                        {late ? (
                          <span style={boardLate}>{t("orders.board.late", "Kasni")}</span>
                        ) : o.requestedDeliveryDate ? (
                          <span style={{ fontSize: 10.5, color: "var(--ink-3)", flex: "none" }}>
                            {relTime(o.requestedDeliveryDate, locale, t)}
                          </span>
                        ) : null}
                      </div>
                    </button>
                  );
                })
              )}
              {/* Kolona pokazuje vrh spiska; koliko ih ukupno ima piše u zaglavlju. */}
              {col.orders.length < col.count && (
                <button type="button" className="btn sm" style={{ width: "100%" }}
                        onClick={() => naJos(col.status)}>
                  {t("orders.board.more", {
                    shown: col.orders.length,
                    total: col.count,
                    defaultValue: "Prikaži još ({{shown}} od {{total}})",
                  })}
                </button>
              )}
          </KanbanColumn>
        );
      })}
    </div>
  );
}

function KpiCard({
  label,
  value,
  accent,
  bg,
  icon,
  onClick,
  active,
  hint,
  danger,
}: {
  label: string;
  value: number;
  accent: string;
  bg: string;
  icon: React.ReactNode;
  onClick?: () => void;
  active?: boolean;
  hint?: string;
  danger?: boolean;
}) {
  const clickable = !!onClick;
  return (
    <div
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      style={{
        background: "var(--surface)",
        border: `1px solid ${active ? accent : "var(--border)"}`,
        boxShadow: active ? `0 0 0 3px ${bg}` : "var(--shadow-1)",
        borderRadius: 10,
        padding: "14px 16px",
        display: "flex",
        flexDirection: "column",
        gap: 6,
        cursor: clickable ? "pointer" : "default",
        transition: "transform .15s ease, box-shadow .15s ease, border-color .15s ease",
      }}
      onMouseEnter={(e) => {
        if (clickable) e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
      }}
      title={hint}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 12, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.3 }}>{label}</span>
        <span className="ord-kpi-icon" style={{ background: bg, color: accent, width: 28, height: 28, borderRadius: 8, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
          {icon}
        </span>
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color: danger && value > 0 ? "var(--err)" : "var(--ink)", fontVariantNumeric: "tabular-nums" }}>
        {value}
      </div>
      {hint && <div style={{ fontSize: 11.5, color: "var(--ink-4)" }}>{hint}</div>}
    </div>
  );
}

function Chip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: active ? "var(--brand-primary)" : "var(--surface)",
        color: active ? "var(--on-primary)" : "var(--ink-2)",
        border: `1px solid ${active ? "var(--brand-primary)" : "var(--border)"}`,
        padding: "6px 12px",
        borderRadius: 999,
        fontSize: 12.5,
        cursor: "pointer",
        fontWeight: 500,
        transition: "all .12s ease",
      }}
    >
      {label}
    </button>
  );
}


function fmtDate(iso: string, locale: string): string {
  return new Date(iso).toLocaleDateString(intlLocale(locale), { day: "numeric", month: "short", year: "numeric" });
}

function relTime(iso: string, locale: string, t: TFunction): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return t("orders.time.justNow", "just now");
  if (min < 60) return t("orders.time.minAgo", "{{count}} min ago", { count: min });
  const h = Math.floor(min / 60);
  if (h < 24) return t("orders.time.hrAgo", "{{count}} hr ago", { count: h });
  const d = Math.floor(h / 24);
  if (d < 7) return t("orders.time.dAgo", "{{count}}d ago", { count: d });
  return new Date(iso).toLocaleDateString(intlLocale(locale), { day: "numeric", month: "short" });
}

function statusKey(status: OrderStatus): string {
  if (status === "IN_REVIEW") return "inReview";
  return status.toLowerCase();
}

const pageHead: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12, flexWrap: "wrap" };
const subText: React.CSSProperties = { fontSize: 13, color: "var(--ink-3)", marginTop: 4 };
const kpiGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 };
const chipBar: React.CSSProperties = { display: "flex", gap: 8, flexWrap: "wrap" };
const filterBar: React.CSSProperties = { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" };
const boardEmpty: React.CSSProperties = { fontSize: 12, color: "var(--ink-4)", textAlign: "center", padding: "18px 0" };
const boardCard: React.CSSProperties = {
  // Razmak drži omotač, kao na kartici ponude; ranije ga je nosilo svako dete svojim `marginTop`.
  display: "flex", flexDirection: "column", gap: 6, width: "100%", textAlign: "left",
  background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8,
  padding: "10px 12px", cursor: "pointer", boxShadow: "var(--shadow-1)",
};
const kupacKartice: React.CSSProperties = { display: "flex", alignItems: "center", gap: 7, minWidth: 0 };
const imeKupca: React.CSSProperties = {
  fontSize: 13, fontWeight: 600, color: "var(--ink)", lineHeight: 1.3,
  display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
};
const iznosKartice: React.CSSProperties = {
  fontSize: 14, fontWeight: 700, color: "var(--ink)", fontVariantNumeric: "tabular-nums",
};
const podnozjeKartice: React.CSSProperties = {
  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6,
  paddingTop: 6, borderTop: "1px solid var(--border)",
};
const brojKartice: React.CSSProperties = {
  fontFamily: "var(--mono, monospace)", fontSize: 10, color: "var(--ink-4)",
  flex: "1 1 auto", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
};
const boardLate: React.CSSProperties = { fontSize: 10.5, fontWeight: 700, color: "var(--err)", background: "var(--err-bg)", padding: "1px 7px", borderRadius: 999 };
const searchWrap: React.CSSProperties = { position: "relative", flex: "1 1 280px", display: "flex", alignItems: "center" };
const searchIcon: React.CSSProperties = { position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", width: 16, height: 16, display: "inline-flex", color: "var(--ink-3)", pointerEvents: "none" };
const tbl: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const th: React.CSSProperties = { textAlign: "left", padding: "11px 12px", background: "var(--surface-2)", color: "var(--ink-3)", fontWeight: 600, fontSize: 11.5, textTransform: "uppercase", letterSpacing: 0.4, borderBottom: "1px solid var(--border)" };
const td: React.CSSProperties = { padding: "12px", borderBottom: "1px solid var(--border)", verticalAlign: "middle" };
const rowStyle: React.CSSProperties = { transition: "background .12s ease" };
const mono: React.CSSProperties = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12.5, color: "var(--ink-2)" };
const lateChip: React.CSSProperties = { display: "inline-flex", alignItems: "center", gap: 3, marginLeft: 8, padding: "1px 6px", background: "var(--err-bg)", color: "var(--err)", borderRadius: 999, fontSize: 10.5, fontWeight: 600 };
const emptyCard: React.CSSProperties = { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, padding: 32, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" };
